import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../utils/api';

const ColorPicker = ({ label, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input type="color" value={value} onChange={e => onChange(e.target.value)} style={{ width: '40px', height: '36px', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', padding: '2px' }} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ flex: 1, background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontFamily: 'monospace', outline: 'none' }}
        maxLength={7}
      />
    </div>
  </div>
);

const AdminBrandingPage = () => {
  const [form, setForm] = useState({
    primary_color: '#FF6B00',
    secondary_color: '#1A3C6E',
    background_color: '#F7F8FC',
    font_choice: 'Nunito',
    logo_url: '',
    favicon_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const { success, error } = useToast();
  const { theme, updateTheme } = useTheme();

  useEffect(() => {
    setForm({
      primary_color: theme.primaryColor || '#FF6B00',
      secondary_color: theme.secondaryColor || '#1A3C6E',
      background_color: theme.backgroundColor || '#F7F8FC',
      font_choice: theme.fontChoice || 'Nunito',
      logo_url: theme.logoUrl || '',
      favicon_url: theme.faviconUrl || ''
    });
  }, [theme]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminAPI.updateTheme(form);
      updateTheme({
        primaryColor: form.primary_color,
        secondaryColor: form.secondary_color,
        backgroundColor: form.background_color,
        fontChoice: form.font_choice,
        logoUrl: form.logo_url || null,
        faviconUrl: form.favicon_url || null
      });
      success('Theme updated! Changes are live for all students.');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { error('Failed to save theme.'); }
    finally { setLoading(false); }
  };

  const handleReset = () => {
    setForm({ primary_color: '#FF6B00', secondary_color: '#1A3C6E', background_color: '#F7F8FC', font_choice: 'Nunito', logo_url: '', favicon_url: '' });
  };

  return (
    <AdminShell
      title="Branding & Theme"
      subtitle="Changes apply instantly to all student screens"
      actions={
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleReset} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Reset to Default</button>
          <button onClick={handleSave} disabled={loading} style={{ background: saved ? '#1B7F3E' : '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
            {loading ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '20px' }}>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '16px' }}>🎨 Colour Palette</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <ColorPicker label="Primary Colour (Buttons, Accents)" value={form.primary_color} onChange={v => setForm(p => ({ ...p, primary_color: v }))} />
              <ColorPicker label="Secondary Colour (Sidebar, Headings)" value={form.secondary_color} onChange={v => setForm(p => ({ ...p, secondary_color: v }))} />
              <ColorPicker label="Background Colour (Page BG)" value={form.background_color} onChange={v => setForm(p => ({ ...p, background_color: v }))} />
            </div>
            <div style={{ marginTop: '12px', padding: '10px 12px', background: '#FEF3C7', borderRadius: '8px', fontSize: '11px', color: '#7C2D12', fontWeight: 600 }}>
              ⚠️ Ensure colours have sufficient contrast for readability. Avoid very light colours.
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '16px' }}>🖼️ Logo & Favicon</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>Logo URL</label>
                <input
                  type="url"
                  value={form.logo_url}
                  onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))}
                  style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }}
                  placeholder="https://mypersonalguruji.com/logo.png"
                />
                <div style={{ fontSize: '10px', color: '#64748B', marginTop: '3px' }}>Upload logo to your Hostinger file manager and paste the URL here. Recommended: PNG, max 100px height.</div>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>Favicon URL</label>
                <input
                  type="url"
                  value={form.favicon_url}
                  onChange={e => setForm(p => ({ ...p, favicon_url: e.target.value }))}
                  style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }}
                  placeholder="https://mypersonalguruji.com/favicon.ico"
                />
              </div>
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '16px' }}>🔤 Font</div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>Body Font</label>
              <select
                value={form.font_choice}
                onChange={e => setForm(p => ({ ...p, font_choice: e.target.value }))}
                style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
              >
                {['Nunito', 'Poppins', 'Lato', 'Hind', 'Mukta'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <div style={{ fontSize: '10px', color: '#64748B', marginTop: '3px' }}>All fonts are from Google Fonts — loaded automatically on the platform.</div>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div>
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '16px', position: 'sticky', top: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>👁️ Live Preview</div>

            {/* Mini sidebar */}
            <div style={{ background: form.secondary_color, borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                {form.logo_url
                  ? <img src={form.logo_url} alt="Logo" style={{ height: '28px', borderRadius: '6px' }} />
                  : <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: form.primary_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'white', fontSize: '12px' }}>G</div>
                }
                <div style={{ fontWeight: 800, fontSize: '11px', color: form.primary_color }}>MyPersonalGuruji</div>
              </div>
              {['Dashboard', 'My Subjects', 'Revisions'].map((item, i) => (
                <div key={item} style={{ padding: '5px 8px', borderRadius: '6px', background: i === 0 ? form.primary_color : 'transparent', color: i === 0 ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                  {item}
                </div>
              ))}
            </div>

            {/* Mini dashboard */}
            <div style={{ background: form.background_color, borderRadius: '8px', padding: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, marginBottom: '8px' }}>Namaste, Arjun! 👋</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                {['68 Mastered', '5 Due Today'].map(s => (
                  <div key={s} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px', fontSize: '10px', fontWeight: 700 }}>{s}</div>
                ))}
              </div>
              <button style={{ width: '100%', background: form.primary_color, color: 'white', border: 'none', borderRadius: '6px', padding: '8px', fontSize: '11px', fontWeight: 800, cursor: 'default' }}>
                Revise Now
              </button>
            </div>

            {/* Colour swatches */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              {[
                { color: form.primary_color, label: 'Primary' },
                { color: form.secondary_color, label: 'Secondary' },
                { color: form.background_color, label: 'BG', border: true }
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: s.color, border: s.border ? '2px solid #E2E8F0' : '2px solid white' }}></div>
                  <div style={{ fontSize: '9px', color: '#64748B', fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminBrandingPage;
